class YevoriCartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-drawer="cart"]');
    if (!this.drawer) return;
    this.revision = 0;
    this.busy = false;
    document.addEventListener('click', (event) => {
      if (!this.busy && event.target.closest('[data-drawer-open="cart"]')) this.refresh().catch(() => this.showError());
    });
    this.drawer.addEventListener('click', (event) => {
      const step = event.target.closest('[data-cart-step]');
      const remove = event.target.closest('[data-cart-remove]');
      if (!step && !remove) return;
      event.preventDefault();
      const form = (step || remove).closest('[data-cart-quantity-form]');
      if (remove) {
        this.change(form, 0);
        return;
      }
      const current = this.readQuantity(form);
      if (current !== null) this.change(form, Math.max(0, current + Number(step.dataset.cartStep)));
    });
    this.drawer.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-cart-quantity-form]')) return;
      event.preventDefault();
      const quantity = this.readQuantity(event.target);
      if (quantity !== null) this.change(event.target, quantity);
    });
  }

  endpoint(path) {
    return window.yevoriConversion.cartEndpoint(path);
  }

  readQuantity(form) {
    const input = form?.elements.quantity;
    if (!input || !input.reportValidity() || input.value.trim() === '') return null;
    const quantity = Number(input.value);
    return Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : null;
  }

  showError(message) {
    const error = this.drawer.querySelector('[data-cart-error]');
    const text = error.querySelector('[data-cart-error-message]');
    if (!error.dataset.defaultMessage) error.dataset.defaultMessage = text.textContent;
    text.textContent = message || error.dataset.defaultMessage;
    error.hidden = false;
  }

  async refresh({ preserveError = false } = {}) {
    const revision = ++this.revision;
    const url = new URL(this.endpoint('cart'));
    url.searchParams.set('section_id', 'yevori-cart-drawer');
    const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Cart rendering failed');
    const html = await response.text();
    if (revision !== this.revision) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
    if (!next) throw new Error('Cart content unavailable');
    const active = document.activeElement;
    const key = active?.closest('[data-cart-line]')?.dataset.cartLine;
    const focusName = active?.getAttribute('name');
    const focusStep = active?.dataset.cartStep;
    const focusUpdate = active?.matches('[data-cart-update]');
    this.drawer.querySelector('[data-cart-content]').replaceWith(next);
    if (!preserveError) this.drawer.querySelector('[data-cart-error]').hidden = true;
    if (key && !this.drawer.hidden) {
      const line = Array.from(next.querySelectorAll('[data-cart-line]')).find((item) => item.dataset.cartLine === key);
      const target = focusStep ? line?.querySelector(`[data-cart-step="${focusStep}"]`) : focusName === 'quantity' ? line?.querySelector('[name="quantity"]') : focusUpdate ? line?.querySelector('[data-cart-update]') : null;
      (target || this.drawer.querySelector('[data-drawer-close]')).focus();
    }
  }

  async open(trigger) {
    await this.refresh();
    if (!window.aureliaTheme || !this.drawer?.isConnected) throw new Error('Cart drawer unavailable');
    window.aureliaTheme.openDrawer('cart', trigger);
  }

  async change(form, quantity) {
    const key = form?.elements.id?.value;
    if (this.busy || !Number.isSafeInteger(quantity) || quantity < 0 || typeof key !== 'string' || !key.trim()) return;
    this.busy = true;
    ++this.revision;
    this.drawer.setAttribute('aria-busy', 'true');
    this.drawer.querySelector('[data-cart-error]').hidden = true;
    this.drawer.querySelectorAll('[name="checkout"]').forEach((button) => { button.disabled = true; });
    try {
      const response = await fetch(this.endpoint('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const payload = await response.json();
      if (!response.ok) {
        const message = payload?.description || payload?.message;
        const rejection = new Error('Cart change rejected');
        if (typeof message === 'string') rejection.customerMessage = message;
        throw rejection;
      }
      await this.refresh();
      await window.yevoriConversion.refreshCartCount();
      const status = this.drawer.querySelector('[data-cart-status]');
      status.textContent = status.dataset.updated;
    } catch (error) {
      this.showError(error.customerMessage);
      // The server may have accepted the change before the connection failed.
      // Re-read the cart; never automatically repeat a cart mutation.
      await this.refresh({ preserveError: true }).catch(() => {});
    } finally {
      this.busy = false;
      this.drawer.removeAttribute('aria-busy');
      this.drawer.querySelectorAll('[name="checkout"]').forEach((button) => { button.disabled = false; });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => { window.yevoriCart = new YevoriCartDrawer(); });
